<template>
  <div
    ref="cont"
    class="eqchart gw_hvr text-noselect"
    :data-tst="`${tstPrefix}eqchart`"
    @mouseleave="emptyHighlight(), toggleTooltip(false)"
    @touchend="emptyHighlight(), toggleTooltip(false)"
  >
    <slot name="message"></slot>
    <div class="eqchart_wrapper">
      <slot></slot>
      <ChartAxes
        class="eqchart_axis-y"
        :style="styleAxes"
        :hasX="false"
      />
      <div
        v-show="showTooltip"
        class="eqchart_floating-content no-events"
      >
        <div
          ref="tooltip"
          class="gw_box csst_follow-content text-medium clr-white"
          style="position: absolute; z-index: 6"
        >
          <ChartLegendPlayers2
            class="eqchart_tooltip"
            :players="players"
            style="
              position: static;
              grid-template-columns: 1fr 1fr;
              --legend-size: 1rem;
              --legend-circle-size: 0.75rem;
            "
          >
            <template
              v-for="(point, index) in tooltipPoints"
              :key="index"
              #[`player_${index}`]
            >
              <div class="ml-2 f-center">
                <CardSymbols
                  style="--size: var(--legend-size)"
                  :colorDefault="true"
                  :cardsIds="point.hand"
                />
                <div class="ml-2 mr-auto">{{ numFixedUnit(point.eq * 100, 'eq') }}%</div>
              </div>
            </template>
          </ChartLegendPlayers2>
        </div>
        <div
          v-show="showTooltip"
          ref="tooltip_line"
          class="eqchart_tooltip-line eqchart_tooltip-line_vertical"
          style="bottom: 0; position: absolute"
        >
          <div class="eqchart_tooltip-line_tick">{{ tickPercentageX }}</div>
        </div>
      </div>
      <ChartLegendControls
        v-if="hasControls"
        :title="hasTitle ? $t('tabs.eq_chart') : ''"
        :hasFullscreen="!skeleton && hasFullscreen"
        :hasResetZoom="!!zoom"
        style="inset: var(--legend-controls-inset); z-index: 4"
        @mouseenter="toggleTooltip(false)"
        @touchstart="toggleTooltip(false)"
        @click:fullscreen="openEqChartDialog"
        @click:resetZoom="resetZoom"
      >
        <GuideTooltip
          v-if="hasGuide"
          guide="eq_chart"
          style="position: absolute; left: -0.75rem"
        />
      </ChartLegendControls>
      <ChartLegendPlayers2
        v-if="!skeleton"
        :players="players"
        class="eqchart_legend"
        style="--legend-inset: var(--legend-players-inset)"
      />
      <HorizolnalScroller
        ref="scroller"
        style="
          left: var(--axis-space);
          max-width: calc(100% - var(--axis-space));
          --clr-arrow-back: transparent;
          --arrow-back-opacity: 0.25;
          --arrow-left-inset: auto auto auto 0;
          --arrow-right-inset: auto 0 auto auto;
        "
        :style="`--hscroller-inner-overflow-x: ${
          zoom ? 'auto' : 'unset'
        };--hscroller-inner-overflow-y:  ${zoom ? 'hidden' : 'unset'};`"
        class="eqchart_scrolller"
        :innerCssClass="`h-100 w-100 z-index_1`"
        :hasDragscroll="false"
        :speed="3"
        :innerId="'eq_chart_scroll_cont'"
        tstPrefix="eqchart"
      >
        <div
          class="eqchart_chart"
          :class="{ eqchart_skeleton: skeleton }"
          :style="zoomStyle"
        >
          <ChartSvg2
            ref="chart"
            class="eqchart_svg"
            :points="playersPointsXY"
            :style="`--axis-spaceX: 0px; --axis-spaceY: 1rem;${styleSvg || ''}`"
            :pointsActiveXY="pointsActiveXY"
            :pointsPinnedXY="[]"
            @mouseenter="e => toggleTooltip(true, e)"
            @touchstart="e => toggleTooltip(true, e)"
            @mousemove="handleMousemove"
            @touchmove="handleMousemove"
          />
          <ChartAxes
            :style="styleAxes"
            :XTicks="axisTicksX"
            :hasY="false"
          />
          <SelectSpaceOverlay
            v-if="!mobile"
            v-show="!zoomDisabled"
            ref="select_overlay"
            :disabled="zoomDisabled"
            style="z-index: 4; top: 0; height: calc(100% - var(--axis-space))"
            @mousedown="toggleTooltip(false)"
            @mouseup="toggleTooltip(true)"
            @mousemove="handleMousemove"
            @mouseenter="e => toggleTooltip(true, e)"
            @update:selection="handleUpdateSelection"
          />
        </div>
      </HorizolnalScroller>
    </div>
  </div>
</template>

<script>
import { nextTick } from 'vue'
import CardSymbols from '@/components/CardSymbols/CardSymbols'
import HorizolnalScroller from '@/components/HorizolnalScroller/HorizolnalScroller'
import GuideTooltip from '@/components/Study/Switchers/GuideTooltip'
import format_mix from '@/mixins/format_mix'
import eq_chart_mix2 from '@/components/Charts/LineCharts/eq_chart_mix2'
import float_mix from '@/mixins/float_mix'
import ChartSvg2 from '@/components/Charts/LineCharts/ChartSvg2'
import ChartAxes from '@/components/Charts/LineCharts/ChartAxes'
import ChartLegendPlayers2 from '@/components/Charts/LineCharts/ChartLegendPlayers2'
import SelectSpaceOverlay from '@/components/Charts/LineCharts/SelectSpaceOverlay'
import ChartLegendControls from '@/components/Charts/LineCharts/ChartLegendControls'
import throttle from 'lodash/throttle'

export default {
  name: 'EquityChartComplex2',
  components: {
    ChartAxes,
    ChartSvg2,
    ChartLegendPlayers2,
    SelectSpaceOverlay,
    ChartLegendControls,
    CardSymbols,
    HorizolnalScroller,
    GuideTooltip,
  },
  mixins: [format_mix, eq_chart_mix2, float_mix],
  props: {
    players: { type: Array, required: true },
    hands: Array,
    activeHands: { type: Array, default: () => [] },
    player: { type: String, default: null },
    skeleton: Boolean,
    rangeKey: String,
    styleAxes: { default: () => '--axis-space: 1rem;' },
    styleSvg: String,
    hasFullscreen: { type: Boolean, default: true },
    hasTitle: { type: Boolean, default: true },
    hasControls: { type: Boolean, default: true },
    hasGuide: { type: Boolean, default: true },
    filteredPlayersHandsIds: { default: () => [] },
    simpleView: Boolean,
    tstPrefix: { type: String, default: '' },
  },
  emits: ['mouseenter:point', 'combos:mouseenter'],
  data() {
    return {
      zoom: 0,
      tooltipPoints: [],
      tickPercentageX: 0,
      showTooltip: false,
      setupTooltipThrottled: null,
    }
  },
  created() {
    this.setupTooltipThrottled = throttle(
      function setupTooltipHandler(e, boundary) {
        requestAnimationFrame(() => {
          const { tooltip, tooltip_line } = this.$refs
          if (!tooltip || !tooltip_line) return
          this.fl_followMouse(e, boundary, tooltip, {
            offsetX: 8,
            offsetY: this.mobile ? -24 : 8,
            padding: 8,
            strategy: 'absolute',
            placement: 'top-start',
          })
          this.fl_followMouse(e, boundary, tooltip_line, {
            offsetX: 0,
            offsetY: 0,
            padding: 0,
            strategy: 'absolute',
          })

          const { percentX } = this.getHoveredPPP(e)
          this.tickPercentageX = this.numFixed(percentX * 100)

          // Get points for all players at current hover position
          this.tooltipPoints = this.playersPointsXY
            .map(({ pointsXY }) => this.getVirtualPoint(pointsXY, percentX))
            .filter(point => point)

          // Emit combo information for each player
          const comboData = this.tooltipPoints.reduce((acc, point) => {
            if (point?.orig_hand) {
              acc[point.player] = point.orig_hand
            }
            return acc
          }, [])
          this.$emit('combos:mouseenter', comboData)
        })
      },
      60,
      { trailing: false },
    )
  },
  methods: {
    toggleTooltip(bool, e) {
      if (this.skeleton) return
      if (bool) {
        nextTick(() => {
          this.showTooltip = true
          if (e) {
            nextTick(() => {
              this.handleMousemove(e)
            })
          }
        })
        return
      }
      this.showTooltip = false
    },
    resetZoom() {
      this.zoom = 0
      nextTick(() => {
        this.checkOverflow()
      })
    },
    handleMousemove(e) {
      if (this.skeleton) return
      if (!this.showTooltip) return

      const boundary = e.currentTarget // event.currentTarget is only available while the event is being handled
      if (!boundary) return

      this.setupTooltipThrottled(e, boundary)
    },
    emptyHighlight() {
      const emptyComboData = this.players.reduce((acc, player, idx) => {
        acc[idx] = null
        return acc
      }, [])
      this.$emit('combos:mouseenter', emptyComboData)
      if (this.setupTooltipThrottled?.cancel) {
        this.setupTooltipThrottled.cancel()
      }
    },
    getVirtualPoint(points, hoveredPPP) {
      const res = points.find((i, idx) => {
        const pointBefore = points[idx - 1]
        const pointAfter = points[idx + 1]

        const halfLeft = (i.x - (pointBefore?.x || 0)) / 2
        const halfRight = ((pointAfter?.x || 0) - i.x) / 2

        const beforeX = i.x - halfLeft
        const afterX = i.x + halfRight

        const r = hoveredPPP >= beforeX && hoveredPPP <= afterX

        return r
      })
      return res
    },
    getHoveredPPP(e) {
      const scroller = this.$refs.scroller.$refs.container.getBoundingClientRect()
      const chart = this.$refs.chart.$refs.svgchart.getBoundingClientRect()
      const scrollCont = document.getElementById('eq_chart_scroll_cont')
      const { scrollLeft } = scrollCont

      if (e?.touches) {
        e = {
          clientX: e?.touches[0]?.clientX,
          clientY: e?.touches[0]?.clientY,
        }
      }
      const { clientX, clientY } = e

      const pixelY = clientY - chart.y
      const pixelX = clientX - chart.x

      const scrollerX = scroller.x
      const hoveredPxX = clientX - scrollerX + scrollLeft
      const percentX = hoveredPxX / chart.width
      const percentY = 1 - pixelY / chart.height

      return {
        percentX,
        percentY,
        pixelY,
        pixelX,
        clientY,
        clientX,
      }
    },

    handleUpdateSelection({ percent, end }) {
      const part = 1 / percent
      this.zoom += part
      this.zoom = this.zoom > 12 ? 12 : this.zoom

      nextTick(() => {
        const scrollCont = document.getElementById('eq_chart_scroll_cont')
        const chart = this.$refs.chart.$refs.svgchart.getBoundingClientRect()
        scrollCont.scrollLeft = chart.width * end
        this.checkOverflow()
      })
    },
    checkOverflow() {
      if (this.$refs.scroller) {
        this.$refs.scroller.checkOverflow()
      }
    },
    getTicks(num) {
      const res = [...Array(num).keys()].map(idx => this.numFixed((100 / num) * idx))
      return [...res, 100]
    },
    openEqChartDialog() {
      this.$store.dispatch('router_set_dialogs', 'chart-eq-dialog')
    },
  },
  computed: {
    playersPointsXY() {
      return this.players.map((player, index) => ({
        pointsXY: this.getPlayerPoints(index, player.relative_postflop_position, this.rangeKey),
        handsPointsXY: this.getFilteredPoints(
          this.getPlayerPoints(index, player.relative_postflop_position, this.rangeKey),
          this.filteredPlayersHandsIds[index] || [],
        ),
      }))
    },
    zoomStyle() {
      const zoom = this.zoom * 100
      const res = `width: calc(${zoom + 100}%)`
      return res
    },
    zoomDisabled() {
      return this.zoom === 12
    },
    axisTicksX() {
      let ticks = 48
      const b = [
        [12, 48],
        [10, 40],
        [8, 32],
        [6, 24],
        [4, 16],
        [2, 8],
        [1, 4],
      ]
      b.forEach(([z, t]) => {
        if (this.zoom < z) {
          ticks = t
        }
      })
      return this.getTicks(ticks)
    },
    globals_action() {
      return this.$store.getters.globals_action
    },
    mobile() {
      return this.$store.getters.globals_mobile
    },
  },
  watch: {
    hands() {
      this.resetZoom()
    },
    globals_action(a) {
      if (a === 'reset_graph_zoom') {
        this.resetZoom()
        this.$store.dispatch('globals_action_reset')
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.eqchart {
  --axis-space: 1rem;
  --legend-controls-inset: -1.125rem auto auto 1.75rem;
  --legend-players-inset: 1.25rem auto auto 1.875rem;
  position: relative;
  flex: 1;
  display: flex;

  &_wrapper {
    position: relative;
    flex: 1;
    display: flex;
  }

  &_chart {
    flex: 1;
    height: 100%;
    width: 100%;
    border-radius: 4px;
    position: relative;
  }

  --clr-tick: var(--clr-grey-2);
  --clr-tick-back: var(--clr-black-1);
  --tick-height: 1rem;
  --tick-inset-vertical: auto auto -1px auto;
  --tick-inset-horizontal: auto auto auto -0.5rem;

  &_tooltip-line {
    position: absolute;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;

    &_vertical {
      width: 0;
      height: 100%;
      top: 0 !important;
      border-right: var(--clr-grey-2) dashed 1px;

      .eqchart_tooltip-line_tick {
        inset: var(--tick-inset-vertical);
      }
    }

    &_tick {
      color: var(--clr-tick);
      height: var(--tick-height);
      background: var(--clr-tick-back);
      position: absolute;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  &_skeleton {
    .eqchart {
      &_legend {
        display: none;
      }
    }
  }
}
</style>
